/**
 * Listings service — list membership (`record_listings`), the join between a
 * list record and the records it holds. Not one of the six owned-row field
 * relationships (link/tagging/attachment/contact/time/source): a listing is
 * membership in a list, not a value of a field on the listed record, so it
 * has no `schema_key` and doesn't route through `applyRelationOps`.
 *
 * `update`/`updateMany` reuse `computeListingChanges` — the same column-vs-
 * jsonb partition `computeRecordChanges` is for records: `is_primary`,
 * `label`, `position` are columns; a field declared by the list's LISTING
 * schema (`list.data.listing_schema_id` — the schema the read path resolves
 * `listing.context` through) merges into `context`. Fields of the LISTED
 * record's type schema do NOT live here — the read path routes those to the
 * record itself — and the list record's OWN `schema_id` fields live in
 * `list.data`, not context. When the list DOES declare a listing schema, an
 * undeclared bare key is refused rather than written somewhere nothing
 * reads. When it declares NONE, there's nothing to validate against, so any
 * key is allowed — a deliberate exception for real, actively-read
 * Luma/Airtable sync data on ~86 legacy schema-less lists (see
 * `assertDeclaredContextKeys` in collections/listings.ts), not a decision
 * that listing context needs no validation. The pre-reads select
 * `list { data }` to make the declared-schema check possible; add/addMany
 * fetch it on demand when a context bag has keys, so the insert door
 * enforces the same rule.
 */

import type { ResultOf } from "@graphql-typed-document-node/core"
import { assertDeclaredContextKeys, computeListingChanges } from "../collections/listings.js"
import { declaredSchemaFieldKeys } from "../collections/records.js"
import {
  addRecordListing,
  createManyRecordListings,
  fetchListByParentAndData,
  fetchListContextSchema,
  fetchListingsToCopy,
  fetchListMaxPosition,
  fetchRecordListingById,
  fetchRecordListingsByIds,
  removeMultipleRecordListings,
  removeRecordListing,
  updateManyRecordListings,
  updateRecordListing,
} from "../gql/listings.js"
import type { Mutate, Query } from "../index.js"
import type { CreatedRecord, createRecordsService } from "./records.js"
import { createSpans } from "./spans.js"

/** The list record `ensureList` returns — the columns a caller needs to show or
 *  populate it, as fetchListByParentAndData selects them. */
export type ListRecord = ResultOf<typeof fetchListByParentAndData>["lists"][number]

export interface EnsureListInput {
  /** The record the list hangs off (e.g. the organization an employee list belongs to). */
  parentId: number
  /** `data.record_type` — the type of record the list holds. */
  recordType: string
  /** `data.listing_role` — what the list means for its parent (e.g. "employee"). */
  listingRole: string
  /** Name for the list when it has to be created; ignored when one exists. */
  name: string
}
/** A listing row as every write here returns it: RecordListingFields, the
 *  same row an entry carries as its `listing`. */
export type ListingItem = NonNullable<
  ResultOf<typeof addRecordListing>["insert_record_listings_one"]
>

/** A listing as the pre-reads select it: the row plus its list's `data`, which
 *  the context-key guard reads. */
type GuardedListing = NonNullable<ResultOf<typeof fetchRecordListingById>["listing"]>

export interface AddListingInput {
  listId: number
  recordId: number
  position?: number | null
  isPrimary?: boolean
  label?: string | null
  context?: Record<string, unknown> | null
}

/**
 * `is_primary`/`label`/`position`/`context` are columns; any other bare key
 * merges into `context`. When the list declares a LISTING schema
 * (data.listing_schema_id), that key must be declared by it — validated the
 * same way records.ts's `update()` validates `data` keys, not an open bag.
 * When the list declares none, any key is accepted (see
 * assertDeclaredContextKeys). A field on the LISTED record belongs in
 * `records.update(listing.record_id, …)`.
 */
export type ListingChanges = Record<string, unknown>

/**
 * A new list was created, but filling it failed. `list` is that list, so the
 * caller can remove it: creating and filling are two writes, and an empty copy
 * left behind looks like a successful duplicate.
 */
export class ListFillError extends Error {
  readonly list: CreatedRecord
  constructor(list: CreatedRecord, cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause), { cause })
    this.name = "ListFillError"
    this.list = list
  }
}

export function createListingsService({
  query,
  mutate,
  records,
}: {
  query: Query
  mutate: Mutate
  records: Pick<ReturnType<typeof createRecordsService>, "create" | "get">
}) {
  // Membership is what a `listing`-tracking field logs (a deal's stage).
  const spans = createSpans({ query, mutate })

  // The pre-reads join `list { data }` for the schema guard; that rider is
  // not part of ListingItem and must not leak into public returns — a no-op
  // update would otherwise return a differently-shaped row than a written one.
  const stripGuardJoin = (row: GuardedListing): ListingItem => {
    const { list: _list, ...rest } = row
    return rest
  }

  const getRaw = async (id: number): Promise<GuardedListing> => {
    const listing = (await query(fetchRecordListingById, { id })).listing
    if (!listing) throw new Error(`Listing not found: #${id}`)
    return listing
  }

  // The append base: one past the list's current max position. Explicit
  // positions preserve input order and land after existing rows — a null
  // position is NOT append-safe (nulls sort last but newest-FIRST within the
  // null block, and same-transaction rows share created_at).
  //
  // EXCEPT on a legacy all-null-position list (rows exist, max is null):
  // any numeric position sorts BEFORE the whole null block under
  // { position: asc }, so "append at 0" would jump the queue to the TOP.
  // True append is impossible while siblings are null — return null and let
  // the new row join the block under the created_at ordering the list
  // already lives by, matching how the frontend inserts into such lists.
  // Known limit: read-then-insert append can interleave under concurrent
  // adders; a DB-side DEFAULT or per-list lock if that ever matters.
  const nextPosition = async (listId: number): Promise<number | null> => {
    const agg = (await query(fetchListMaxPosition, { listId })).record_listings_aggregate?.aggregate
    const max = agg?.max?.position
    if (max != null) return max + 1
    return (agg?.count ?? 0) > 0 ? null : 0
  }

  // The insert door's half of the context-key rule: no listing row exists to
  // pre-read yet, so resolve the listing schema from the LIST record's data.
  // Skipped entirely when the context bag is empty — the common path pays no
  // extra read.
  const assertContextKeysDeclared = async (
    listId: number,
    context: Record<string, unknown> | null | undefined
  ): Promise<void> => {
    const keys = Object.keys(context ?? {})
    if (!keys.length) return
    const list = (await query(fetchListContextSchema, { id: listId })).list
    // records_by_pk is null for a nonexistent OR RLS-hidden id — say that,
    // rather than letting the schema guard misdiagnose it as schema-less.
    if (!list) throw new Error(`List not found: #${listId} — or not visible to you.`)
    // `data` is jsonb: its keys aren't typed by the selection.
    const schemaId = (list.data as { listing_schema_id?: number | null } | null)?.listing_schema_id
    assertDeclaredContextKeys(keys, schemaId, `list #${listId}`)
  }

  const add = async (input: AddListingInput): Promise<ListingItem> => {
    await assertContextKeysDeclared(input.listId, input.context)
    const res = await mutate(addRecordListing, {
      listId: input.listId,
      recordId: input.recordId,
      isPrimary: input.isPrimary ?? false,
      position: input.position ?? (await nextPosition(input.listId)),
      // ?? not ||: an explicit empty-string label is a real (if odd) value,
      // not "not provided" — only null/undefined should fall back.
      label: input.label ?? null,
      context: input.context ?? {},
    })
    const created = res.insert_record_listings_one
    // ON CONFLICT DO NOTHING on (list_id, record_id) — same idempotency addMany
    // has — means a record already on the list returns no row here, not a
    // thrown unique-constraint violation. add()'s contract is always-a-row-or-
    // throw, so this still throws, but names the actual cause instead of the
    // generic "no row" — addMany reports this case without throwing at all.
    if (!created) {
      // Two causes share this shape: ON CONFLICT DO NOTHING matched an
      // existing row, or the insert LANDED but the row falls outside the
      // caller's select filter (record_listings inserts aren't
      // permission-filtered the way selects are). Don't assert one.
      throw new Error(
        `Record ${input.recordId} is already on list ${input.listId} — or the list ` +
          `isn't visible to you (the row may have been created regardless). Use ` +
          `addMany if you want this reported rather than thrown.`
      )
    }
    await spans.stampListings(
      [{ recordId: created.record_id, from: null, to: created.list_id }],
      created.created_at
    )
    return created
  }

  /**
   * Add many records to a list in one insert. ON CONFLICT DO NOTHING on
   * (list_id, record_id) — a record already on the list is a no-op, not an
   * error, and doesn't appear in `added`. Positions are always explicit and
   * preserve input order: `startPosition + index` when given, else appended
   * after the list's current max (`nextPosition`). A null position was NOT
   * append-safe — nulls sort last but newest-FIRST within the null block
   * under `{ position: asc, created_at: desc }`, and rows inserted in one
   * transaction share `created_at`, so input order was lost entirely.
   *
   * `alreadyListedIds` is computed as `recordIds - added`, so it collapses
   * two cases: the record genuinely already on the list (the common one),
   * and an RLS-denied insert. A follow-up SELECT against record_listings
   * would be needed to tell them apart.
   */
  const addMany = async (
    listId: number,
    recordIds: number[],
    opts: { context?: Record<string, unknown>; startPosition?: number | null } = {}
  ): Promise<{ added: ListingItem[]; alreadyListedIds: number[] }> => {
    if (!recordIds.length) return { added: [], alreadyListedIds: [] }
    await assertContextKeysDeclared(listId, opts.context)

    const base = opts.startPosition ?? (await nextPosition(listId))
    const objects = recordIds.map((recordId, index) => ({
      list_id: listId,
      record_id: recordId,
      // base === null: legacy all-null list — see nextPosition. Numbering
      // from 0 here would put the batch at the top instead of appending.
      position: base === null ? null : base + index,
      context: opts.context ?? {},
    }))

    const res = await mutate(createManyRecordListings, { objects })
    const result = res.insert_record_listings
    if (!result) throw new Error("addMany: Hasura returned no result")

    const added = result.returning
    if (added.length) {
      await spans.stampListings(
        added.map((row) => ({ recordId: row.record_id, from: null, to: row.list_id })),
        added[0]?.created_at
      )
    }
    const addedIds = new Set(added.map((row) => row.record_id))
    const alreadyListedIds = recordIds.filter((id) => !addedIds.has(id))
    return { added, alreadyListedIds }
  }

  /**
   * Copy every membership of one list onto another — duplicating a list. Each
   * row keeps its own record, primary flag, label, position and context. A
   * record already on the destination is skipped (ON CONFLICT DO NOTHING) and
   * reported in `alreadyListedIds`, same as `addMany`.
   *
   * The copied context keys are validated against the DESTINATION's listing
   * schema, like any insert: a copy is a write onto that list.
   *
   * `recordIds` copies only those records' memberships.
   */
  const copy = async (
    fromListId: number,
    toListId: number,
    opts: { recordIds?: number[] } = {}
  ): Promise<{ added: ListingItem[]; alreadyListedIds: number[] }> => {
    const listed = (await query(fetchListingsToCopy, { listId: fromListId })).listings
    if (!listed) throw new Error(`copy: Hasura returned no listings for list #${fromListId}`)
    const wanted = opts.recordIds ? new Set(opts.recordIds) : null
    const source = wanted ? listed.filter((row) => wanted.has(row.record_id)) : listed
    if (!source.length) return { added: [], alreadyListedIds: [] }

    const contextKeys = Object.fromEntries(
      source.flatMap((row) => Object.keys(row.context ?? {})).map((k) => [k, true])
    )
    await assertContextKeysDeclared(toListId, contextKeys)

    const objects = source.map((row) => ({
      list_id: toListId,
      record_id: row.record_id,
      is_primary: row.is_primary ?? false,
      label: row.label,
      position: row.position,
      context: row.context ?? {},
    }))
    const res = await mutate(createManyRecordListings, { objects })
    const result = res.insert_record_listings
    if (!result) throw new Error("copy: Hasura returned no result")

    const added = result.returning
    const addedIds = new Set(added.map((row) => row.record_id))
    const alreadyListedIds = source.map((r) => r.record_id).filter((id) => !addedIds.has(id))
    return { added, alreadyListedIds }
  }

  const remove = async (id: number): Promise<{ id: number }> => {
    const res = await mutate(removeRecordListing, { id })
    const deleted = res.delete_record_listings_by_pk
    if (!deleted) throw new Error(`Listing ${id} not found for delete`)
    await spans.stampListings([{ recordId: deleted.record_id, from: deleted.list_id, to: null }])
    return { id: deleted.id }
  }

  /**
   * `missingIds` is computed as `ids - removedIds`, so it collapses two
   * cases: the id genuinely doesn't exist, and an RLS-denied delete. A
   * follow-up SELECT against record_listings would be needed to tell them
   * apart.
   */
  const removeMany = async (
    ids: number[]
  ): Promise<{ removedIds: number[]; missingIds: number[] }> => {
    if (!ids.length) return { removedIds: [], missingIds: [] }

    const res = await mutate(removeMultipleRecordListings, { ids })
    const result = res.delete_record_listings
    if (!result) throw new Error("removeMany: Hasura returned no result")

    if (result.returning.length) {
      await spans.stampListings(
        result.returning.map((row) => ({ recordId: row.record_id, from: row.list_id, to: null }))
      )
    }
    const removedIds = result.returning.map((row) => row.id)
    const removed = new Set(removedIds)
    const missingIds = ids.filter((id) => !removed.has(id))
    return { removedIds, missingIds }
  }

  const update = async (id: number, changes: ListingChanges): Promise<ListingItem> => {
    const listing = await getRaw(id)
    const { _set, hasChanges } = computeListingChanges(listing, changes)
    if (!hasChanges) return stripGuardJoin(listing)

    const res = await mutate(updateRecordListing, { id, _set })
    const updated = res.update_record_listings_by_pk
    if (!updated) throw new Error(`Listing ${id} not found for update`)
    return updated
  }

  /**
   * Move a listing to another list — a deal changing pipeline stage. The row
   * keeps its id, record, label and context; only its list (and, when given,
   * its position) changes. Omitting `position` leaves it as it was.
   *
   * Context is not re-validated against the destination's listing schema:
   * nothing in it is being written, and `update` likewise checks only the keys
   * a write names.
   */
  const move = async (
    id: number,
    listId: number,
    opts: { position?: number | null } = {}
  ): Promise<ListingItem> => {
    const from = (await getRaw(id)).list_id
    const _set: Record<string, unknown> = { list_id: listId }
    if (opts.position !== undefined) _set.position = opts.position
    const res = await mutate(updateRecordListing, { id, _set })
    const moved = res.update_record_listings_by_pk
    if (!moved) throw new Error(`Listing ${id} not found for move`)
    await spans.stampListings([{ recordId: moved.record_id, from, to: listId }], moved.updated_at)
    return moved
  }

  /**
   * Batch update. One read for every current row (fetchRecordListingsByIds,
   * `where: id _in`), not one query per id — each row is still diffed against
   * its OWN current state individually afterward, since computeListingChanges'
   * context merge target differs per listing; batching the READ doesn't
   * require batching that computation too. `updated` preserves the caller's
   * input order — a no-op entry keeps its slot (its current row, unchanged,
   * same as update()'s own always-return-the-row rule), and an id that goes
   * missing (never existed, or the mutation's WHERE matched no row) is left
   * out of `updated` and named in `missingIds` instead, without disturbing
   * anyone else's position.
   *
   * `rejected` is a THIRD, distinct outcome from `missingIds`: an entry whose
   * row exists but whose changes computeListingChanges refused (e.g. a
   * context key the list's listing schema doesn't declare). "the row isn't
   * there" and "the row's there but this write is invalid" are different
   * problems with different fixes, so they're never merged into one bucket —
   * a caller reading `rejected.error` gets the same message update() would
   * have thrown, just without one bad entry taking down the whole batch:
   * this is a batch API whose contract is per-item reporting (missingIds
   * already exists for exactly that reason), so a validation failure on one
   * entry must not destroy the siblings that would have succeeded.
   */
  const updateMany = async (
    rawUpdates: Array<{ id: number; changes: ListingChanges }>
  ): Promise<{
    updated: ListingItem[]
    missingIds: number[]
    rejected: Array<{ id: number; error: string }>
  }> => {
    if (!rawUpdates.length) return { updated: [], missingIds: [], rejected: [] }

    // Two entries for the same id would each plan independently against the
    // SAME pre-read `current` row, so whichever mutation the batch applied
    // second would silently overwrite the first's context merge instead of
    // combining with it. Merge same-id changes (last write per key wins)
    // before planning, preserving each id's FIRST position in the input.
    const order: number[] = []
    const mergedById = new Map<number, ListingChanges>()
    for (const u of rawUpdates) {
      if (!mergedById.has(u.id)) order.push(u.id)
      mergedById.set(u.id, { ...(mergedById.get(u.id) ?? {}), ...u.changes })
    }
    const updates = order.map((id) => ({ id, changes: mergedById.get(id)! }))

    const { listings } = await query(fetchRecordListingsByIds, { ids: order })
    const currentById = new Map(listings.map((r) => [r.id, r]))

    const missingIds: number[] = []
    const rejected: Array<{ id: number; error: string }> = []
    const plans = updates
      .map((u) => {
        const current = currentById.get(u.id)
        if (!current) {
          missingIds.push(u.id)
          return null
        }
        // A guard throw (e.g. an undeclared context key) is per-entry, not
        // batch-fatal: letting it propagate out of this `.map()` would abort
        // every sibling update, valid ones included, for one bad entry —
        // the wrong failure mode for a batch API that already reports
        // per-item outcomes via `missingIds`. Report it the same way,
        // distinguishably, and keep planning the rest.
        try {
          return { id: u.id, current, ...computeListingChanges(current, u.changes) }
        } catch (err) {
          rejected.push({ id: u.id, error: err instanceof Error ? err.message : String(err) })
          return null
        }
      })
      .filter((p): p is NonNullable<typeof p> => p !== null)

    const toWrite = plans.filter((p) => p.hasChanges)

    // Row a written id resolved to, keyed for the order-preserving pass below.
    // No entry means: not written (no-op — that plan's OWN current row is the
    // answer), or written but Hasura's WHERE matched no row (added to
    // missingIds here, same treatment as a pre-read miss).
    const rowById = new Map<number, ListingItem>()
    if (toWrite.length) {
      const res = await mutate(updateManyRecordListings, {
        updates: toWrite.map((p) => ({ where: { id: { _eq: p.id } }, _set: p._set })),
      })
      const per = res.update_record_listings_many
      if (!per) throw new Error("updateMany: Hasura returned no result")
      toWrite.forEach((p, i) => {
        const row = per[i]?.returning?.[0]
        if (row) rowById.set(p.id, row)
        else missingIds.push(p.id)
      })
    }

    const updated: ListingItem[] = []
    for (const p of plans) {
      if (!p.hasChanges) {
        updated.push(stripGuardJoin(p.current))
      } else {
        const row = rowById.get(p.id)
        if (row) updated.push(row)
      }
    }
    return { updated, missingIds, rejected }
  }

  /** Reorder is a position-only batch update. Position is only meaningful
   *  within one list, so the ids must all belong to the same list_id — a
   *  stale or mixed selection would otherwise silently rewrite a DIFFERENT
   *  list's ordering. The pre-read costs one batched query on data updateMany
   *  re-reads anyway; correctness beats the round trip here. */
  const reorder = async (
    positions: Array<{ id: number; position: number }>
  ): Promise<Awaited<ReturnType<typeof updateMany>>> => {
    const seen = new Map<number, number>()
    for (const p of positions) {
      const holder = seen.get(p.position)
      if (holder != null) {
        throw new Error(
          `reorder: duplicate position ${p.position} (listings ${holder} and ${p.id}) — ` +
            `each listing needs a distinct position.`
        )
      }
      seen.set(p.position, p.id)
    }

    const ids = positions.map((p) => p.id)
    const rows = (await query(fetchRecordListingsByIds, { ids })).listings
    const listIds = new Set(rows.map((r) => r.list_id))
    if (listIds.size > 1) {
      throw new Error(
        `reorder: the ids span two lists (${[...listIds].join(", ")}) — position is ` +
          `only meaningful within one list; reorder one list at a time.`
      )
    }

    return updateMany(positions.map((p) => ({ id: p.id, changes: { position: p.position } })))
  }

  /**
   * Find-or-create the list under `parentId` whose data holds `{ listing_role,
   * record_type }` — e.g. an organization's employee list, which exists only
   * for some organizations and is made lazily. The find skips archived lists
   * and takes the lowest id when several match. The create goes through
   * `records.create` (type `list`, shared, `listing_template: null`, slug left
   * to the database), so the planner and schema rules apply as for any record.
   *
   * Accepted race: find-then-create is not atomic, so two concurrent callers
   * that both find nothing each create a list; the lowest-id-first find then
   * makes every later caller settle on the older one.
   */
  const ensureList = async (input: EnsureListInput): Promise<ListRecord> => {
    const found = await query(fetchListByParentAndData, {
      parentId: input.parentId,
      match: { listing_role: input.listingRole, record_type: input.recordType },
    })
    const existing = found.lists[0]
    if (existing) return existing

    const created = await records.create({
      type: "list",
      name: input.name,
      parent_id: input.parentId,
      visibility: "shared",
      data: {
        record_type: input.recordType,
        listing_role: input.listingRole,
        listing_template: null,
      },
    })
    return {
      id: created.id,
      name: created.name,
      slug: created.slug,
      parent_id: created.parent_id ?? input.parentId,
      data: created.data,
    }
  }

  /**
   * Duplicate a list: a new list record named "<name> copy" with the source's
   * description, visibility, parent, schema and declared `data` — record type,
   * listing schema (shared, so copied context keeps its columns), filter and
   * saved view — then its members. `data` keys no schema declares are
   * left behind: core refuses to write them, and many legacy lists hold some.
   *
   * `recordIds` clones just those records. Members are copied with their
   * listing; matches that aren't members are added as plain members. The
   * filter is NOT carried — it would pull every other match back in.
   */
  const duplicateList = async (
    listId: number,
    opts: { recordIds?: number[] } = {}
  ): Promise<CreatedRecord> => {
    const source = await records.get(listId)
    if (source.type !== "list") throw new Error(`duplicateList: #${listId} is not a list`)

    const declared = declaredSchemaFieldKeys(source)
    const data = Object.fromEntries(
      Object.entries((source.data ?? {}) as Record<string, unknown>).filter(
        ([key]) => declared.has(key) && !(opts.recordIds && key === "filter")
      )
    )

    const created = await records.create({
      type: "list",
      name: `${source.name} copy`,
      description: source.description,
      visibility: source.visibility,
      parent_id: source.parent_id,
      schema_id: source.schema_id,
      data,
    })

    try {
      const { added, alreadyListedIds } = await copy(listId, created.id, opts)
      if (opts.recordIds) {
        const copied = new Set([...added.map((row) => row.record_id), ...alreadyListedIds])
        await addMany(
          created.id,
          opts.recordIds.filter((id) => !copied.has(id))
        )
      }
    } catch (error) {
      throw new ListFillError(created, error)
    }
    return created
  }

  const get = async (id: number): Promise<ListingItem> => stripGuardJoin(await getRaw(id))

  /** Listings by id, in one read; an id with no row is missing from the result. */
  const getMany = async (ids: number[]): Promise<ListingItem[]> => {
    if (!ids.length) return []
    const { listings } = await query(fetchRecordListingsByIds, { ids: [...new Set(ids)] })
    return listings.map(stripGuardJoin)
  }

  return {
    get,
    getMany,
    add,
    addMany,
    copy,
    duplicateList,
    ensureList,
    remove,
    removeMany,
    update,
    updateMany,
    move,
    reorder,
  }
}
